/**
 * ConvertToInvoiceButton - T183
 *
 * Button to convert an estimate to an invoice
 */
import { useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Button } from '../ui';
import { useConvertEstimate, type Estimate } from '../../hooks/useEstimates';
import { useToastHelpers } from '../ui';

interface ConvertToInvoiceButtonProps {
  estimate: Estimate;
}

export function ConvertToInvoiceButton({ estimate }: ConvertToInvoiceButtonProps) {
  const navigate = useNavigate();
  const toast = useToastHelpers();
  const convertEstimate = useConvertEstimate();
  const [isConverting, setIsConverting] = useState(false);

  // Only allow conversion for sent or accepted estimates
  const canConvert = estimate.status === 'sent' || estimate.status === 'accepted';

  if (!canConvert) {
    return null;
  }

  const handleConvert = async () => {
    if (!confirm('Möchten Sie dieses Angebot in eine Rechnung umwandeln?')) {
      return;
    }

    setIsConverting(true);
    try {
      const invoice = await convertEstimate.mutateAsync(estimate.id);
      toast.success('Rechnung erfolgreich erstellt');
      navigate({ to: '/invoices/$id', params: { id: invoice.id } });
    } catch {
      toast.error('Fehler beim Erstellen der Rechnung');
    } finally {
      setIsConverting(false);
    }
  };

  return (
    <Button
      onClick={handleConvert}
      disabled={isConverting || convertEstimate.isPending}
    >
      {isConverting ? 'Erstelle...' : 'In Rechnung umwandeln'}
    </Button>
  );
}
